import DeletePopover from "@/app/shared/delete-popover";
import EyeIcon from "@/components/icons/eye";
import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import { IbyUser, IVoucher } from "@/utilities/types/Voucher";
import dayjs from "dayjs";
import { useState } from "react";
import { ActionIcon, Badge, Modal, Tooltip } from "rizzui";

type VoucherSortBy =
  | "code"
  | "used"
  | "promotionId"
  | "createdAt"
  | "updatedAt"
  | "byUserId";

type Columns = {
  data: IbyUser[];
  sortConfig?: SortConfig<VoucherSortBy>;
  onDeleteItem: (id: string) => void;
  onHeaderCellClick?: (value: string) => void;
  handleReset: () => void;
  refresh?: () => void;
};

// Voucher Details Modal Component
const VoucherDetailsModal = ({
  isOpen,
  onClose,
  voucherData,
}: {
  isOpen: boolean;
  onClose: () => void;
  voucherData: IVoucher;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Voucher Details</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <h3 className="font-semibold text-lg">Code: {voucherData.code}</h3>
          </div>

          <div className="border-t pt-4 mt-2 col-span-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium">
                  {voucherData.used ? "Used" : "Not Used"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Promotion</p>
                <p className="font-medium">
                  {voucherData.promotion?.code || "Unknown"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium">
                  {voucherData.createdAt
                    ? dayjs(voucherData.createdAt).format("MMMM DD, YYYY HH:mm")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Updated At</p>
                <p className="font-medium">
                  {voucherData.updatedAt
                    ? dayjs(voucherData.updatedAt).format("MMMM DD, YYYY HH:mm")
                    : "-"}
                </p>
              </div>
              {voucherData.heldAt && (
                <div>
                  <p className="text-sm text-gray-500">Held At</p>
                  <p className="font-medium">
                    {dayjs(voucherData.heldAt).format("MMMM DD, YYYY HH:mm")}
                  </p>
                </div>
              )}
              {voucherData.xeroPayment && (
                <div>
                  <p className="text-sm text-gray-500">Xero Payment ID</p>
                  <p className="font-medium">{voucherData.xeroPaymentId}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded-md text-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Simple view button component with its own state
const VoucherViewButton = ({ voucherData }: { voucherData: IVoucher }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <Tooltip
        size="sm"
        content={"View Voucher"}
        placement="top"
        color="invert"
      >
        <ActionIcon size="sm" variant="outline" onClick={openModal}>
          <EyeIcon className="h-4 w-4" />
        </ActionIcon>
      </Tooltip>

      {isModalOpen && (
        <VoucherDetailsModal
          isOpen={isModalOpen}
          onClose={closeModal}
          voucherData={voucherData}
        />
      )}
    </>
  );
};

export const getColumns = ({
  sortConfig,
  onDeleteItem,
  onHeaderCellClick,
}: Columns) => {
  return [
    {
      title: (
        <HeaderCell
          title="User"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "byUserId"
          }
        />
      ),
      dataIndex: "byUser",
      key: "byUser",
      onHeaderCell: () => onHeaderCellClick?.("byUserId"),
      render: (byUser: IbyUser) => {
        return (
          <span className="font-medium">{`${byUser.firstName} ${byUser.lastName}`}</span>
        );
      },
    },

    {
      title: <HeaderCell title="Email" align="left" />,
      dataIndex: "byUser",
      key: "byUser",
      render: (byUser: IbyUser) => {
        return <span className="font-medium">{byUser.email}</span>;
      },
    },

    {
      title: <HeaderCell title="Phone" align="left" />,
      dataIndex: "byUser",
      key: "byUser",
      render: (byUser: IbyUser) => {
        return <span className="font-medium">{byUser.phone}</span>;
      },
    },

    {
      title: (
        <HeaderCell
          title="Account Status"
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "used"
          }
        />
      ),
      dataIndex: "byUser",
      key: "byUser",
      align: "center",
      render: (byUser: IbyUser) => {
        return (
          <Badge
            variant="flat"
            className="w-24 py-1 capitalize"
            color={byUser.statusAccount === "active" ? "success" : "danger"}
          >
            {byUser.statusAccount}
          </Badge>
        );
      },
    },

    {
      title: (
        <HeaderCell
          title="Used on"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
          }
        />
      ),
      dataIndex: "byUser",
      key: "byUser",
      align: "center",
      onHeaderCell: () => onHeaderCellClick?.("createdAt"),
      render: (byUser: IbyUser) => {
        return byUser.createdAt
          ? dayjs(byUser.createdAt).format("MMMM DD, YYYY")
          : "-";
      },
    },

    // {
    //   title: (
    //     <HeaderCell
    //       className="ps-3"
    //       title={<span className="whitespace-nowrap">Actions</span>}
    //     />
    //   ),
    //   dataIndex: "action",
    //   key: "action",
    //   width: 120,
    //   render: (_: string, row: IVoucher) => {
    //     return (
    //       <div className="flex items-center justify-end gap-3 pe-4">
    //         <VoucherViewButton voucherData={row} />
    //
    //         <DeletePopover
    //           title={`Delete Voucher?`}
    //           description={`Are you sure you want to delete voucher with code "${row.code}"?`}
    //           onDelete={() => onDeleteItem(row.id)}
    //         />
    //       </div>
    //     );
    //   },
    // },
  ];
};
